import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router';
import logo from 'src/assets/svgs/duke-logo.svg';
import Button from 'src/components/Common/@electron/Button';
import { DukeTagline } from 'src/components/Common/Svgs';
import track from 'src/lib/Analytics';
import GoogleTagManager from 'src/lib/Utils/GoogleTagManager';
import { MagnifyingGlass } from './MagnifyingGlass';
// Renders a route-not-found message when no route is available from Sitecore
// The JSS equivalent of a 404 Not Found page.

// This is invoked from RouteHandler when Sitecore returns no valid route data.
// The NotFound component receives the Layout Service Context data, but no route data.
// This can be used to power parts of your site, such as navigation, from LS context additions
// without losing the ability to render them on your 404 pages :)

const footerProps = {
  copyright: { value: '© Duke Energy Corporation. All Rights Reserved.' },
  bottomLinks: [
    {
      href: '/legal/privacy',
      text: 'Privacy',
    },

    {
      href: '/legal/terms-of-use',
      text: 'Terms of Use',
    },

    {
      href: '/customer-service/accessibility',
      text: 'Accessibility',
    },
  ],
};

const Tagline = () => (
  <div className="px-16 md:px-24 py-24 md:py-32 border-b border-teal-darker">
    <div className="container-4xl">
      <DukeTagline
        className="mx-auto h-16 w-auto text-white fill-current"
        aria-label="Building a Smarter Energy Future"
      />
    </div>
  </div>
);

const BottomLinks = () => (
  <nav aria-label="Legal" className="lg:flex lg:justify-end lg:flex-grow py-12 lg:py-0">
    <ul className="lg:flex lg:space-x-24">
      {footerProps.bottomLinks.map(({ text, ...link }, index) => (
        <li key={index}>
          <a
            className="block lg:inline-block px-16 py-12 lg:p-0 no-underline hover:underline"
            {...link}
          >
            {text}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

const Copyright = () => (
  <p className="px-16 py-24 lg:p-0 border-t border-teal-darker lg:border-t-0">
    {footerProps.copyright.value}
  </p>
);

const Footer = () => (
  <footer>
    <div className="bg-blue text-white">
      <Tagline />
      <div className="lg:px-24 lg:py-24 text-center text-sm lg:text-xs">
        <div className="container-4xl lg:flex lg:flex-row-reverse lg:justify-center">
          <BottomLinks />
          <Copyright />
        </div>
      </div>
    </div>
  </footer>
);

const PNGFooter = () => (
  <footer className="relative png-bg-blue text-white text-center" role="contentinfo">
    <div className="lg:px-24 lg:py-24 text-center text-sm lg:text-xs">
      <div className="container-4xl lg:flex lg:flex-row-reverse lg:justify-center">
        <nav className="lg:flex lg:justify-end lg:flex-grow py-12 lg:py-0" aria-label="Legal">
          <ul className="lg:flex lg:space-x-24">
            <li>
              <a
                className="block lg:inline-block px-16 py-12 lg:p-0 no-underline hover:underline"
                href="https://www.piedmontng.com/privac"
              >
                Privacy
              </a>
            </li>
            <li>
              <a
                className="block lg:inline-block px-16 py-12 lg:p-0 no-underline hover:underline"
                href="https://www.piedmontng.com/terms-of-use"
              >
                Terms of Use
              </a>
            </li>
            <li>
              <a
                className="block lg:inline-block px-16 py-12 lg:p-0 no-underline hover:underline"
                href="https://www.piedmontng.com/sitemap"
              >
                Sitemap
              </a>
            </li>
          </ul>
        </nav>
        <p className="lg:flex-grow-0 px-16 py-24 lg:p-0 border-t border-teal-darker lg:border-t-0">
          &copy; Piedmont Natural Gas. All Rights Reserved.
        </p>
      </div>
    </div>
  </footer>
);

const Header = () => (
  <header className="z-header sticky md:relative top-0">
    <div className="js-site-header-main px-16 md:px-24 py-12 sm:py-16 xl:py-24 bg-white border-b border-gray">
      <div className="container-full flex justify-center items-center h-full">
        <div className="flex items-center gap-16 xl:gap-24 z-10">
          <a href="/home" className="flex items-center">
            <img className="logo-de-sm lg:logo-de" src={logo} width={250} height={110} alt="" />
          </a>
        </div>
      </div>
    </div>
  </header>
);

const PNGHeader = () => (
  <header className="png-bg-blue" role="banner">
    <div className="px-16 md:px-24 py-12 sm:py-16 xl:py-24">
      <div className="container-full flex items-center h-full justify-center">
        <a
          className="flex items-center"
          href="https://www.duke-energy.com"
          aria-label="Duke Energy homepage"
        >
          <svg
            className="logo-de-sm lg:logo-de fill-current text-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 128 29.311"
            aria-hidden="true"
            focusable="false"
          >
            <title>Piedmont Natural Gas</title>
            <path
              fill="#fff"
              d="m46.476,3.502h3.677c2.217,0,3.677,1.179,3.677,3.115v.028c0,2.119-1.768,3.214-3.859,3.214h-2.386v3.466h-1.109V3.502Zm3.537,5.347c1.628,0,2.695-.87,2.695-2.161v-.028c0-1.404-1.053-2.133-2.638-2.133h-2.484v4.323h2.428Zm5.473-5.558h1.235v1.193h-1.235v-1.193Zm.07,2.779h1.081v7.256h-1.081v-7.256Zm6.582,6.484c.996,0,1.698-.407,2.288-1.025l.674.603c-.73.814-1.614,1.361-2.989,1.361-1.993,0-3.621-1.53-3.621-3.789,0-2.105,1.474-3.789,3.48-3.789,2.147,0,3.382,1.712,3.382,3.846,0,.098,0,.21-.014.379h-5.754c.154,1.53,1.277,2.414,2.554,2.414Zm2.119-3.284c-.126-1.319-.884-2.442-2.316-2.442-1.249,0-2.204,1.038-2.358,2.442h4.673Zm2.358.449v-.028c0-2.372,1.726-3.775,3.466-3.775,1.347,0,2.217.73,2.779,1.544V3.081h1.081v10.245h-1.081v-1.46c-.589.87-1.445,1.614-2.779,1.614-1.74,0-3.466-1.376-3.466-3.761Zm6.287-.014v-.028c0-1.67-1.277-2.793-2.624-2.793-1.403,0-2.554,1.039-2.554,2.793v.028c0,1.712,1.179,2.807,2.554,2.807,1.347,0,2.624-1.137,2.624-2.807Zm3.242-3.635h1.081v1.221c.477-.716,1.123-1.376,2.344-1.376,1.179,0,1.937.632,2.33,1.446.519-.8,1.291-1.446,2.554-1.446,1.67,0,2.695,1.123,2.695,2.919v4.491h-1.081v-4.238c0-1.404-.702-2.189-1.881-2.189-1.095,0-2.007.814-2.007,2.245v4.182h-1.067v-4.266c0-1.361-.716-2.161-1.867-2.161s-2.021.954-2.021,2.288v4.14h-1.081v-7.256Zm12.687,3.663v-.028c0-2.049,1.6-3.789,3.789-3.789,2.175,0,3.775,1.712,3.775,3.761v.028c0,2.049-1.614,3.789-3.803,3.789-2.176,0-3.761-1.712-3.761-3.761Zm6.456,0v-.028c0-1.558-1.165-2.835-2.695-2.835-1.572,0-2.653,1.277-2.653,2.807v.028c0,1.558,1.151,2.821,2.681,2.821,1.572,0,2.667-1.263,2.667-2.793Zm2.849-3.663h1.081v1.263c.477-.786,1.235-1.418,2.484-1.418,1.754,0,2.779,1.179,2.779,2.905v4.505h-1.081v-4.238c0-1.347-.73-2.189-2.007-2.189-1.249,0-2.175.912-2.175,2.273v4.154h-1.081v-7.256Zm8.814,5.333v-4.379h-1.011v-.954h1.011v-2.189h1.081v2.189h2.302v.954h-2.302v4.238c0,.884.491,1.207,1.221,1.207.365,0,.674-.07,1.052-.252v.926c-.379.196-.786.309-1.305.309-1.165,0-2.049-.576-2.049-2.049Zm-60.474,4.449h1.039l6.189,7.873v-7.873h1.081v9.824h-.884l-6.344-8.056v8.056h-1.081v-9.824Zm10.147,7.719v-.028c0-1.558,1.263-2.372,3.102-2.372.926,0,1.572.126,2.218.309v-.225c0-1.291-.786-1.965-2.147-1.965-.856,0-1.53.21-2.203.519l-.323-.884c.8-.365,1.572-.617,2.639-.617,1.024,0,1.824.281,2.344.8.505.505.758,1.207.758,2.133v4.435h-1.067v-1.081c-.505.645-1.333,1.235-2.611,1.235-1.361,0-2.709-.772-2.709-2.26Zm5.333-.561v-.674c-.533-.154-1.235-.309-2.119-.309-1.347,0-2.119.59-2.119,1.46v.028c0,.912.842,1.445,1.824,1.445,1.333,0,2.414-.8,2.414-1.951Zm3.44.744v-4.379h-1.011v-.954h1.011v-2.189h1.081v2.189h2.302v.954h-2.302v4.238c0,.884.491,1.207,1.221,1.207.365,0,.674-.07,1.053-.252v.926c-.379.196-.786.309-1.305.309-1.165,0-2.049-.576-2.049-2.049Zm4.968-.828v-4.505h1.081v4.238c0,1.347.73,2.189,2.007,2.189,1.235,0,2.175-.912,2.175-2.273v-4.154h1.067v7.256h-1.067v-1.263c-.491.786-1.235,1.418-2.484,1.418-1.754,0-2.779-1.179-2.779-2.905Zm8.547-4.505h1.081v1.895c.533-1.207,1.586-2.077,2.961-2.021v1.165h-.084c-1.586,0-2.877,1.137-2.877,3.326v2.891h-1.081v-7.256Zm4.577,5.151v-.028c0-1.558,1.263-2.372,3.102-2.372.926,0,1.572.126,2.218.309v-.225c0-1.291-.786-1.965-2.147-1.965-.856,0-1.53.21-2.203.519l-.323-.884c.8-.365,1.572-.617,2.639-.617,1.024,0,1.824.281,2.344.8.505.505.758,1.207.758,2.133v4.435h-1.067v-1.081c-.505.645-1.333,1.235-2.611,1.235-1.361,0-2.709-.772-2.709-2.26Zm5.333-.561v-.674c-.533-.154-1.235-.309-2.119-.309-1.347,0-2.119.59-2.119,1.46v.028c0,.912.842,1.445,1.825,1.445,1.333,0,2.414-.8,2.414-1.951Zm3.228-7.579h1.081v10.245h-1.081v-10.245Zm7.129,5.361v-.028c0-2.695,1.979-5.08,4.926-5.08,1.656,0,2.68.477,3.663,1.305l-.716.842c-.758-.66-1.6-1.123-2.989-1.123-2.176,0-3.733,1.839-3.733,4.028v.028c0,2.344,1.502,4.084,3.888,4.084,1.123,0,2.161-.435,2.835-.982v-2.442h-2.989v-.996h4.056v3.916c-.912.814-2.287,1.502-3.944,1.502-3.088,0-4.996-2.245-4.996-5.052Zm10.638,2.779v-.028c0-1.558,1.263-2.372,3.102-2.372.926,0,1.572.126,2.218.309v-.225c0-1.291-.786-1.965-2.147-1.965-.856,0-1.53.21-2.203.519l-.323-.884c.8-.365,1.572-.617,2.639-.617,1.024,0,1.824.281,2.344.8.505.505.758,1.207.758,2.133v4.435h-1.067v-1.081c-.505.645-1.333,1.235-2.611,1.235-1.361,0-2.709-.772-2.709-2.26Zm5.333-.561v-.674c-.533-.154-1.235-.309-2.119-.309-1.347,0-2.119.59-2.119,1.46v.028c0,.912.842,1.445,1.825,1.445,1.333,0,2.414-.8,2.414-1.951Zm2.526,1.74l.547-.772c.786.59,1.656.926,2.512.926.87,0,1.502-.449,1.502-1.151v-.028c0-.73-.856-1.01-1.81-1.277-1.137-.323-2.4-.716-2.4-2.049v-.028c0-1.249,1.038-2.077,2.47-2.077.884,0,1.867.309,2.61.8l-.491.814c-.674-.435-1.446-.702-2.147-.702-.856,0-1.403.449-1.403,1.052v.028c0,.688.898.954,1.867,1.249,1.123.337,2.33.772,2.33,2.077v.028c0,1.375-1.137,2.175-2.582,2.175-1.038,0-2.189-.407-3.003-1.067Zm6.683-.739v-.01c0-.991.805-1.821,1.826-1.821s1.816.82,1.816,1.811v.01c0,.991-.805,1.821-1.826,1.821s-1.816-.82-1.816-1.811Zm3.451-.01v-.01c0-.901-.699-1.63-1.625-1.63-.931,0-1.635.739-1.635,1.64v.01c0,.901.699,1.63,1.625,1.63.931,0,1.635-.74,1.635-1.64Zm-2.365-1.006h.85c.252,0,.453.075.579.201.1.1.156.241.156.402v.01c0,.307-.181.498-.443.579l.513.714h-.433l-.463-.659h-.397v.659h-.362v-1.906Zm.835.931c.236,0,.377-.116.377-.297v-.01c0-.191-.141-.297-.377-.297h-.473v.604h.473Z"
            />
            <path
              fill="#fff"
              d="m34.379,26.317l3.458-.32c1.261-.117,2.273-1.543,2.273-3.172V6.495c0-1.629-1.012-3.059-2.273-3.183l-3.458-.335v23.341Z"
            />
            <path
              fill="#fff"
              d="m7.627,28.929l-3.732.365c-2.118.202-3.895-1.425-3.895-3.622V3.652C0,1.455,1.777-.172,3.895.03l6.37.616c-1.274,6.362-4.792,10.969-6.367,17.311-1.107,4.452.296,9.406,3.729,10.972ZM14.721,1.077c1.173,6.259,4.232,10.77,5.515,16.478.862,3.837-.235,8.423-3.166,10.451l16.189-1.584V2.868L14.721,1.077Zm-1.87,27.681c-5.424.533-7.921-5.565-6.575-10.891C7.882,11.509,11.58,6.99,12.613.397c.052-.329.163-.405.238-.397.077.008.186.105.238.444,1.01,6.677,4.412,11.26,5.79,17.146,1.11,4.747-.982,10.671-6.028,11.168Zm4.16-8.587c-.962-4.032-3.308-7.087-4-11.606-.033-.229-.107-.291-.16-.293-.052-.002-.127.054-.162.28-.7,4.515-3.184,7.662-4.251,11.963-.722,2.905.233,6.152,2.572,7.089-.56-.76-.733-1.926-.47-3.011.552-2.275,1.855-3.995,2.225-6.411.019-.121.06-.153.086-.154.027,0,.067.03.086.15.367,2.383,1.633,3.957,2.153,6.104.244,1,.088,2.148-.437,2.983,2.158-1.301,2.985-4.463,2.359-7.094Z"
            />
          </svg>
        </a>
      </div>
    </div>
  </header>
);

const pageTitle = 'Page not found';

const NotFound = () => {
  const { pathname } = useLocation();
  const [isPNGsite, setIsPNGsite] = useState(false);

  useEffect(() => {
    const message = 'We can’t find the page you’re looking for.';
    const prevPage = (window as any).prevPage ?? '';

    track.component({
      category: 'error_404',
      label: prevPage,
      action: `${message} | ${pathname}`,
      event: 'event-load',
    });
  }, []);

  useEffect(() => {
    // the RouteHandler didn't detect domain so use hostname to determine if the site is PNG
    if (window.location.hostname.indexOf('piedmontng.com') !== -1) {
      setIsPNGsite(true);
    } else {
      setIsPNGsite(false);
    }
  }, []);

  if (isPNGsite) {
    return (
      <div className="flex-grow flex flex-col png-bg-blue">
        <Helmet>
          <title>404 Page Not Found | Piedmont Natural Gas</title>
          <meta name="title" content={pageTitle} />
        </Helmet>
        <a
          className="btn btn-secondary fixed left-1/2 top-0 text-teal-darker bg-white transform -translate-x-1/2 -translate-y-full focus:translate-y-16 focus:z-modal"
          href="#main-content"
        >
          Skip to Content
        </a>
        <PNGHeader />
        <main className="relative flex flex-col flex-grow w-full p-16 md:p-24 png-bg-blue">
          <div className="relative flex flex-col flex-grow justify-center items-center container-3xl px-24 py-32 text-white text-center">
            <MagnifyingGlass />
            <h1 className="mt-12 md:mt-32 text-xl-fixed md:text-2xl-fixed">
              We can’t find the page you’re <span className="whitespace-nowrap">looking for.</span>
            </h1>
            <p className="mt-12 md:mt-32 text-md md:text-lg">
              We’ve searched high and low for your page, but it{' '}
              <span className="whitespace-nowrap">can’t be found.</span>
            </p>
            <p className="mt-16 md:mt-32">
              <a className="btn btn-secondary-reversed" href="https://www.piedmontng.com">
                Go to home page
              </a>
            </p>
          </div>
        </main>
        <PNGFooter />
      </div>
    );
  }

  return (
    <div className="flex-grow flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
      </Helmet>
      <a
        className="btn btn-secondary fixed left-1/2 top-0 text-teal-darker bg-white transform -translate-x-1/2 -translate-y-full focus:translate-y-16 focus:z-modal"
        href="#main-content"
      >
        Skip to Content
      </a>
      <Header />
      <main id="main-content" className="flex-grow flex flex-col">
        <section className="flex flex-col flex-grow relative h-full overflow-hidden bg-teal-darker">
          <div className="absolute inset-0 bg-gradient-radial from-transparent to-blue-dark opacity-40" />
          <div className="relative flex flex-col flex-grow justify-center items-center container-3xl px-24 py-32 text-white text-center">
            <MagnifyingGlass />
            <h1 className="mt-12 md:mt-32 text-xl-fixed md:text-2xl-fixed">
              We can’t find the page you’re <span className="whitespace-nowrap">looking for.</span>
            </h1>
            <p className="mt-12 md:mt-32 text-md md:text-lg">
              We’ve searched high and low for your page, but it{' '}
              <span className="whitespace-nowrap">can’t be found.</span>
            </p>
            <p className="mt-16 md:mt-32">
              <Button variant="secondary-reversed" href="/home">
                Go to home page
              </Button>
            </p>
          </div>
        </section>
        <GoogleTagManager />
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
